import * as React from 'react';
import { IConfig } from './types/Config.Type';
import { ILoaderProps } from './map/layer/ILoaderProps';
import { LandUse } from './map/layers/lwwp/contamination/LandUse';
import { UrbanDischarges } from './map/layers/lwwp/contamination/UrbanDischarges';
import { RiverNetwork } from './map/layers/lwwp/lines/RiverNetwork';
import { Nitrate } from './map/layers/lwwp/watershed/Nitrate';
import { Nitrogen } from './map/layers/lwwp/watershed/Nitrogen';
import { Phosphate } from './map/layers/lwwp/watershed/Phosphate';
import { Phosphorus } from './map/layers/lwwp/watershed/Phosphorus';
import { Ecoli } from './map/layers/lwwp/watershed/Ecoli';
import { DinBottom } from './map/layers/lwwp/quality/DinBottom';
import { DinTop } from './map/layers/lwwp/quality/DinTop';
import { ChlaBottom } from './map/layers/lwwp/quality/ChlaBottom';
import { ChlaTop } from './map/layers/lwwp/quality/ChlaTop';

const Config: IConfig = {
  map: {
    mapKey: "pk.eyJ1IjoibG9uZ2xpbmVlbnZpcm9ubWVudCIsImEiOiJjbGF0cHF1ZWUwM2l0M3FwcDcyN3B1YXpmIn0.snFi9yTPEZ5lfQxE3h3Epg",
    greyStyle: null, // "mapbox://styles/longlineenvironment/clatpsjsl003r15okdwsdclmi",
    satelliteStyle: "mapbox://styles/longlineenvironment/clnkins3a001s01qw9k3efaj5/draft",
    longitude: -5.5,
    latitude: 54.55,  
    zoom: 8, 
    minZoom: 0,
    maxZoom: 14,
    bearing: 0,
    pitch: 0,
    max_bounds: [
      [-8,  52.5],    // Southwest (lng/lat)
      [-4,  56.5]     // Northeast (lng/lat)
    ]
  },
  UI: {
    height: 75,
    before_layer: 'waterway-label',
    large_screen: 900,
    load_delay_base: 0,
    load_delay_random: 0,
  },

  legend_caption: "Lough Classification",
  suitability: [
    { name: 'High', color: 'blue' },
    { name: 'Good', color: 'green' },
    { name: 'Moderate', color: 'yellow' },
    { name: 'Poor', color: 'orange' },
    { name: 'Bad', color: 'red' },
  ],  

  datasets: [
    {
      key: 'land_use',
      ids: [ 'land_use' ],
      title: 'Land use',
      description: 'Land use in the Belfast watershed area',
      group: 'contamination',
      renderer: (p: ILoaderProps) => <LandUse {...p}/>
    }, {
      key: 'river_network',
      ids: [],
      title: 'River network',
      description: 'River network simulated by SWAT',
      active: true,
      hidden: true,
      renderer: (p: ILoaderProps) => <RiverNetwork {...p}/>
    }, {
      key: 'urban_discharges',
      ids: [ 'urban_discharges' ],
      title: 'Urban discharges',
      description: 'All urban discharges implemented in the modelling framework',
      group: 'contamination',
      renderer: (p: ILoaderProps) => <UrbanDischarges {...p}/>
    }, {
      key: 'nitrate',
      ids: [ 'nitrate' ],
      title: "Nitrate",
      description: "Nitrate loadings per sub-basin",
      group: "watershed",
      renderer: (p: ILoaderProps) => <Nitrate {...p}/>
    }, {
      key: 'nitrogen',
      ids: [ 'nitrogen' ],
      title: "Nitrogen",
      description: "Total nitrogen loadings per sub-basin",
      group: "watershed",
      renderer: (p: ILoaderProps) => <Nitrogen {...p}/>
    }, {
      key: 'phosphate',
      ids: [ 'phosphate' ],
      title: "Phosphate",
      description: "Phosphate loadings per sub-basin",
      group: "watershed",
      renderer: (p: ILoaderProps) => <Phosphate {...p}/>
    }, {
      key: 'phosphorus',
      ids: [ 'phosphorus' ],
      title: "Phosphorus",
      description: "Total phosphorus loadings per sub-basin",
      group: "watershed",
      renderer: (p: ILoaderProps) => <Phosphorus {...p}/>
    }, {
      key: 'ecoli',
      ids: [ 'ecoli' ],
      title: "E. coli",
      description: "Escherichia coli loadings per sub-basin",
      group: "watershed",
      renderer: (p: ILoaderProps) => <Ecoli {...p}/>
    }, {
      key: 'din_bottom',
      ids: [ 'din_bottom' ],
      title: "Dissolved Inorganic Nitrogen (DIN) in bottom layer",
      description: "Average DIN concentration in winter (Nov-Feb)",
      group: 'quality',
      renderer: (p: ILoaderProps) => <DinBottom {...p}/>
    }, {
      key: 'din_top',
      ids: [ 'din_top' ],
      title: "Dissolved Inorganic Nitrogen (DIN) in top layer",
      description: "Average DIN concentration in winter (Nov-Feb)",
      group: 'quality',
      renderer: (p: ILoaderProps) => <DinTop {...p}/>
    }, {
      key: 'chla_bottom',
      ids: [ 'chla_bottom' ],
      title: "Phytoplankton in bottom layer",
      description: "Chlorophyll Percentile 90 in summer (Mar to Oct)",
      group: 'quality',
      renderer: (p: ILoaderProps) => <ChlaBottom {...p}/>
    }, {
      key: 'chla_top',
      ids: [ 'chla_top' ],
      title: "Phytoplankton in top layer",
      description: "Chlorophyll Percentile 90 in summer (Mar to Oct)",
      group: 'quality',
      renderer: (p: ILoaderProps) => <ChlaTop {...p}/>
    }
  ],
  datagroups: [
    {
      key: 'watershed',
      title: 'Watershed',
      description: 'Nutrients and bacteria loadings',
      exclusive: true,
    }, {
      key: 'quality',
      title: "Belfast Lough water quality",
      description: "Compliance to Water Framework Directive (WFD)",
      exclusive: true
    }, {
      key: 'contamination',
      title: 'Sources of contamination',
      description: 'Land use and location of urban discharges',
      exclusive: false,
    }
  ],
  dictionary: [
    {
      key: 'suitability',
      text: 'Suitability',
      type: 'suitability'
    }, {
      key: "landuse",
      text: "Land use"
    }, {
      key: "type",
      text: "Type"
    }, { 
      key: "dap",
      text: "Location"
    }, {
      key: "subbasin",
      text: "Sub-basin"
    }, {
      key: "area",
      text: "Area",
      type: 'number',
      decimals: 0,
      unit: "ha"
    }, {
      key: "no3_kg_ha",
      text: "Nitrate",
      type: 'number',
      unit: 'kg-N/ha/year',
      decimals: 2
    }, {
      key: "ntot_kg_ha",
      text: "Total nitrogen",
      type: 'number',
      unit: 'kg/ha/year',
      decimals: 2
    }, {
      key: "po4_kg_ha",
      text: "Phosphate",
      type: 'number',
      unit: 'kg/ha/year',
      decimals: 2
    }, {
      key: "ptot_kg_ha",
      text: "Total phosphorus",
      type: 'number',
      unit: 'kg-P/ha/year',
      decimals: 2
    }, {
      key: "ecoli_cfu_ha",
      text: "E. coli",
      type: "number",
      unit: "units/ha/year",
      scientific: true
    }, {
      key: "box",
      text: "Box"
    }, {
      key: "winter_din_bottom",
      text: "DIN conc. (winter avg)",
      unit: <>&#x00B5;mol/L</>,
      type: "number",
      decimals: 2
    }, {
      key: "winter_din_top",
      text: "DIN conc. (winter avg)",
      unit: <>&#x00B5;mol/L</>,
      type: "number",
      decimals: 2
    }, {
      key: "summer_chla_bottom",
      text: "Chlorophyll Percentile 90 (summer)",
      unit: <>&#x00B5;g/L</>,
      type: "number",
      decimals: 2
    }, {
      key: "summer_chla_top",
      text: "Chlorophyll Percentile 90 (summer)",
      unit: <>&#x00B5;g/L</>,
      type: "number",
      decimals: 2
    }
  ]
}

export { Config }
